import React from 'react'

import { useQuery } from '@apollo/client'
import { Box, Form, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import {
  BudgetFragment,
  GetEmployeesForBudgetsDocument,
  UpdateFinanceBudgetMutationVariables,
} from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import MoneyField from 'modules/core/components/MoneyField'
import SelectField from 'modules/core/components/SelectField'
import Selector from 'modules/core/components/Selector'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { fromCents, toCents } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'

import { useBudgetsPage } from 'modules/finance/hooks/useBudgets'
import { periodicityOptions } from 'modules/finance/lib/budgets'

interface EditBudgetFormProps {
  form: FormikProps<UpdateFinanceBudgetMutationVariables>
  budget: BudgetFragment
  setArchiving: React.Dispatch<React.SetStateAction<boolean>>
}

const EditBudgetForm = ({ form, budget, setArchiving }: EditBudgetFormProps) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { legalEntities } = useBudgetsPage(companyId)

  const legalEntity = legalEntities?.find((node) => node.id == budget.legalEntityId)

  const { data } = useQuery(GetEmployeesForBudgetsDocument, {})
  const [archived, setArchived] = React.useState(budget.archived)

  const employees = data?.employees.employees || []

  const setOwnerEmployeeIds = (values: unknown) => {
    form.setValues({
      ...form.values,
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      ownerEmployeeIds: values as string[],
    })
  }

  const setViewerEmployeeIds = (values: unknown) => {
    form.setValues({
      ...form.values,
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      viewerEmployeeIds: values as string[],
    })
  }

  const options = employees.map((employee) => {
    return (
      <Selector.Option
        key={employee.id}
        value={employee.id}
        label={String(employee?.fullName)}
        searchValue={String(employee?.fullName)}
      >
        {employee?.fullName}
      </Selector.Option>
    )
  })

  const canEdit = budget._policies?.update.can
  const disableFields = budget.archived || !canEdit

  return (
    <Box gap="s24">
      <Box gap="s8">
        <Text.Normal weight="semibold">
          {i18n.t('budgets.forms.new_budget.basic_information.title')}
        </Text.Normal>
        <Text.Normal>
          {i18n.t('budgets.forms.new_budget.basic_information.description')}
        </Text.Normal>
      </Box>
      <Form padding="s0">
        {legalEntities.length > 1 && (
          <TextField
            readonly
            label={i18n.t('budgets.forms.new_budget.basic_information.legal_entity')}
            value={String(legalEntity?.legalName)}
            error={form.errors.name}
          />
        )}
        <Box
          gap="s24"
          paddingBottom="s24"
          border={{ bottom: { color: 'grey400', style: 'solid', width: 's1' } }}
        >
          <TextField
            readonly={!canEdit}
            label={i18n.t('budgets.forms.new_budget.basic_information.budget_title')}
            value={form.values.name?.toString()}
            error={form.errors.name}
            onChange={(value) => form.setValues({ ...form.values, name: value })}
          />
          <TextArea
            readonly={disableFields}
            label={i18n.t('budgets.forms.new_budget.basic_information.budget_description')}
            value={String(form.values.description)}
            error={form.errors.description}
            onChange={(value) => form.setValues({ ...form.values, description: value })}
          />
        </Box>

        <Box gap="s8">
          <Text.Normal size="300" weight="semibold">
            {i18n.t('budgets.forms.new_budget.budget_access.title')}
          </Text.Normal>
          <Text.Normal>{i18n.t('budgets.forms.new_budget.budget_access.description')}</Text.Normal>
        </Box>

        <Box paddingBottom="s12" gap="s4">
          <Box gap="s2">
            <Text.Header3>
              {i18n.t('budgets.forms.new_budget.budget_access.budget_owners')}
            </Text.Header3>
            <Text.Normal size="200">
              {i18n.t('budgets.forms.new_budget.budget_access.budget_owners_description')}
            </Text.Normal>
          </Box>
          <Selector.Popover
            isDisabled={disableFields}
            label=""
            value={form.values.ownerEmployeeIds ?? []}
            placeholder={i18n.t('budgets.forms.new_budget.budget_access.select_employees')}
            blankLabel=""
            onChange={setOwnerEmployeeIds}
            inputStyle="badge"
            mandatory={false}
            isMultiSelect
          >
            {options}
          </Selector.Popover>
        </Box>

        <Box
          paddingBottom="s24"
          border={{ bottom: { color: 'grey400', style: 'solid', width: 's1' } }}
          gap="s4"
        >
          <Box gap="s2">
            <Text.Header3>
              {i18n.t('budgets.forms.new_budget.budget_access.budget_viewers')}
            </Text.Header3>
            <Text.Normal size="200">
              {i18n.t('budgets.forms.new_budget.budget_access.budget_viewers_description')}
            </Text.Normal>
          </Box>

          <Selector.Popover
            isDisabled={disableFields}
            label=""
            value={form.values.viewerEmployeeIds ?? []}
            placeholder={i18n.t('budgets.forms.new_budget.budget_access.select_employees')}
            blankLabel=""
            onChange={setViewerEmployeeIds}
            inputStyle="badge"
            mandatory={false}
            isMultiSelect
          >
            {options}
          </Selector.Popover>
        </Box>

        <Box gap="s8">
          <Text.Normal weight="semibold">
            {i18n.t('budgets.forms.new_budget.budget_limits.title')}
          </Text.Normal>
          <Text.Normal>{i18n.t('budgets.forms.new_budget.budget_limits.description')}</Text.Normal>
        </Box>
        <Box flexDirection="row" gap="s32" paddingBottom="s8">
          <DayPicker
            readonly={disableFields}
            label={i18n.t('budgets.forms.new_budget.budget_limits.start_date')}
            value={form.values.effectiveFrom}
            valueFormat="backend_datetime"
            error={form.errors.effectiveFrom?.toString()}
            mode="single"
            onChange={(value) => form.setValues({ ...form.values, effectiveFrom: value })}
          />
          <DayPicker
            readonly={disableFields}
            label={i18n.t('budgets.forms.new_budget.budget_limits.end_date')}
            value={form.values.effectiveUntil}
            valueFormat="backend_datetime"
            minDate={moment(form.values.effectiveFrom).startOf('day')}
            error={form.errors.effectiveUntil?.toString()}
            mode="single"
            onChange={(value) => form.setValues({ ...form.values, effectiveUntil: value })}
          />
        </Box>
        <Box
          flexDirection="row"
          gap="s32"
          paddingBottom="s24"
          border={{ bottom: { color: 'grey400', style: 'solid', width: 's1' } }}
        >
          <SelectField
            readonly
            mandatory
            none={i18n.t('budgets.forms.new_budget.budget_limits.select_time_frame')}
            error={form.errors?.periodicity}
            label={i18n.t('budgets.forms.new_budget.budget_limits.time_frame')}
            options={periodicityOptions()}
            value={String(form.values.periodicity)}
          />
          <MoneyField
            readonly={disableFields}
            currency={budget.currency}
            label={i18n.t('budgets.forms.new_budget.budget_limits.amount_per_time_frame')}
            value={fromCents(form.values.amountCents ? form.values.amountCents : 0).toString()}
            onChange={(value) =>
              form.setValues({ ...form.values, amountCents: toCents(parseInt(value)) })
            }
            error={form.errors.amountCents ? String(form.errors.amountCents) : undefined}
          />
        </Box>
        <Box gap="s12">
          <Text.Normal weight="semibold">
            {budget.archived
              ? i18n.t('budgets.forms.edit_budget.archive.unarchive')
              : i18n.t('budgets.forms.edit_budget.archive.label')}
          </Text.Normal>
          <Box flexDirection="row" gap="s12" justifyContent="center" alignItems="center">
            <Text.Normal>
              {budget.archived
                ? i18n.t('budgets.forms.edit_budget.archive.description_archived')
                : i18n.t('budgets.forms.edit_budget.archive.description_unarchived')}
            </Text.Normal>
            <TogglerCheckbox
              disabled={!canEdit}
              aria-label={i18n.t('budgets.forms.edit_budget.archive.label')}
              label=""
              value={archived}
              onChange={(value) => {
                setArchived(value)
                setArchiving(value)
              }}
            />
          </Box>
        </Box>
      </Form>
    </Box>
  )
}

export default EditBudgetForm
